import { QueryClient } from "@tanstack/react-query";
import { isApiError } from "./errors.js";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      // İstemci hataları (4xx) yeniden denenmez; ağ/sunucu hataları iki kez denenir.
      retry: (failureCount, error) => !(isApiError(error) && error.problem.status < 500) && failureCount < 2,
    },
    mutations: { retry: false },
  },
});

/** Sorgu anahtarları tek yerde: gerçek zamanlı olaylar ve mutasyonlar aynı anahtarları geçersiz kılar. */
export const queryKeys = {
  me: ["me"] as const,
  onboarding: ["onboarding"] as const,
  companies: ["companies"] as const,
  regions: ["regions"] as const,
  distributors: ["distributors"] as const,
  stations: (filters?: Record<string, unknown>) =>
    filters ? (["stations", filters] as const) : (["stations"] as const),
  station: (id: string) => ["stations", "detail", id] as const,
  users: ["users"] as const,
  invitations: ["invitations"] as const,
  roles: ["roles"] as const,
  permissions: ["permissions"] as const,
  sessions: ["sessions"] as const,
  taxRates: ["tax-rates"] as const,
  products: (filters?: Record<string, unknown>) =>
    filters ? (["products", filters] as const) : (["products"] as const),
  productCategories: ["product-categories"] as const,
  stationAssets: (stationId: string) => ["station-assets", stationId] as const,
  strappingTables: (tankId: string) => ["strapping-tables", tankId] as const,
  prices: (filters?: Record<string, unknown>) =>
    filters ? (["prices", filters] as const) : (["prices"] as const),
  priceHistory: (stationId: string, productId: string) =>
    ["prices", "history", stationId, productId] as const,
  priceChangeRequests: (filters?: Record<string, unknown>) =>
    filters ? (["price-change-requests", filters] as const) : (["price-change-requests"] as const),
  priceChangeRequest: (id: string) => ["price-change-requests", "detail", id] as const,
  /** Fiyat grupları (Prompt 16): toplu fiyat değişikliğinde istasyon seçim kısayolu (şirket düzeyi). */
  priceGroups: ["price-groups"] as const,
  /**
   * Muhasebe verisi ŞİRKET bazındadır: anahtarın ikinci parçası hep şirket kimliğidir. `resource` verilmezse şirketin
   * tüm muhasebe önbelleği, `params` verilmezse o kaynağın tüm sorguları geçersiz kılınır.
   */
  accounting: (companyId: string, resource?: AccountingResource, params?: unknown) =>
    resource === undefined
      ? (["accounting", companyId] as const)
      : params === undefined
        ? (["accounting", companyId, resource] as const)
        : (["accounting", companyId, resource, params] as const),
  /** Fiş detayı şirketten bağımsız anahtarlanır: bağlantıyla açılan fişin şirketi yanıt gelmeden bilinmez. */
  accountingEntry: (id?: string) =>
    id ? (["accounting-entry", id] as const) : (["accounting-entry"] as const),
  /**
   * Cari hesaplar: kart kiracı düzeyindedir, bakiye / açık kalem / rapor ŞİRKET bazındadır. `["counterparties"]` öneki
   * modülün tüm önbelleğini geçersiz kılar (hareket, eşleştirme ve iptal birden çok listeyi etkiler).
   */
  counterparties: (companyId?: string | null, resource?: CounterpartyResource, params?: unknown) =>
    companyId === undefined
      ? (["counterparties"] as const)
      : resource === undefined
        ? (["counterparties", companyId] as const)
        : params === undefined
          ? (["counterparties", companyId, resource] as const)
          : (["counterparties", companyId, resource, params] as const),
  /** Kart detayı şirketten bağımsızdır (seçiciler ve fiş satırları yalnızca kimlikle sorar). */
  counterpartyCard: (id?: string) =>
    id ? (["counterparty-card", id] as const) : (["counterparty-card"] as const),
  /**
   * Hazine (kasa, banka, POS, teminat, DBS, kredi): tanımlar ve belgeler ŞİRKET bazındadır. `["treasury"]` öneki
   * modülün tüm önbelleğini geçersiz kılar — bir hareket bakiyeyi, kasa defterini, nakit pozisyonunu ve
   * ekstre kuyruğunu birlikte değiştirir.
   */
  treasury: (companyId?: string | null, resource?: TreasuryResource, params?: unknown) =>
    companyId === undefined
      ? (["treasury"] as const)
      : resource === undefined
        ? (["treasury", companyId] as const)
        : params === undefined
          ? (["treasury", companyId, resource] as const)
          : (["treasury", companyId, resource, params] as const),
  /** Banka kataloğu ve ekstre şablonları kiracı/platform düzeyindedir (şirketten bağımsız). */
  treasuryGlobal: (resource: "banks" | "statement-templates") => ["treasury-global", resource] as const,
  /**
   * Çek / senet: portföy, bordro, karne ve raporlar ŞİRKET bazındadır. `["instruments"]` öneki modülün tüm önbelleğini
   * geçersiz kılar — bir bordro portföyü, takvimi, raporları, karneleri ve (fiş, cari, kasa/banka) diğer modülleri etkiler.
   */
  instruments: (companyId?: string | null, resource?: InstrumentResource, params?: unknown) =>
    companyId === undefined
      ? (["instruments"] as const)
      : resource === undefined
        ? (["instruments", companyId] as const)
        : params === undefined
          ? (["instruments", companyId, resource] as const)
          : (["instruments", companyId, resource, params] as const),
  /** Çek ve bordro detayı kimlikle anahtarlanır (bağlantıyla açılan kaydın şirketi yanıt gelmeden bilinmez). */
  instrumentDetail: (kind: "instrument" | "batch", id?: string) =>
    id ? (["instrument-detail", kind, id] as const) : (["instrument-detail"] as const),
  /**
   * Fatura ve e-belge: ayarlar, seriler, sağlayıcı, faturalar, irsaliyeler, gelen kutusu ve POS talepleri ŞİRKET
   * bazındadır. `["invoicing"]` öneki modülün tüm önbelleğini geçersiz kılar — bir kesinleştirme faturayı, gelen
   * kutusunu, irsaliye bağlarını, talepleri ve (fiş, cari, kasa/POS) diğer modülleri birlikte değiştirir.
   */
  invoicing: (companyId?: string | null, resource?: InvoicingResource, params?: unknown) =>
    companyId === undefined
      ? (["invoicing"] as const)
      : resource === undefined
        ? (["invoicing", companyId] as const)
        : params === undefined
          ? (["invoicing", companyId, resource] as const)
          : (["invoicing", companyId, resource, params] as const),
  /** Fatura / irsaliye / gelen fatura / talep detayı kimlikle anahtarlanır (bağlantıyla açılan kaydın şirketi bilinmez). */
  invoicingDetail: (kind: InvoicingDetailKind, id?: string) =>
    id ? (["invoicing-detail", kind, id] as const) : (["invoicing-detail"] as const),
  /**
   * Onay motoru KİRACI düzeyindedir (şirket kırılımı yoktur): talepler, politikalar, vekâletler ve pano rozeti.
   * `["approvals"]` öneki modülün tümünü geçersiz kılar — bir karar hem listeleri hem rozeti değiştirir.
   */
  approvals: (resource?: ApprovalResource, params?: unknown) =>
    resource === undefined
      ? (["approvals"] as const)
      : params === undefined
        ? (["approvals", resource] as const)
        : (["approvals", resource, params] as const),
  /** Talep detayı kimlikle anahtarlanır (belgeden bağlantıyla açılan talebin kapsamı bilinmez). */
  approvalRequest: (id?: string) =>
    id ? (["approval-request", id] as const) : (["approval-request"] as const),
  /**
   * Giderler: kategoriler kiracı, diğer her şey (belge, dağıtım anahtarı, sayaç, kart, tekrarlayan) ŞİRKET bazındadır.
   * `["expenses"]` öneki modülün tüm önbelleğini geçersiz kılar — bir onay gideri, fişi, cariyi ve hazineyi birlikte değiştirir.
   */
  expenses: (companyId?: string | null, resource?: ExpenseResource, params?: unknown) =>
    companyId === undefined
      ? (["expenses"] as const)
      : resource === undefined
        ? (["expenses", companyId] as const)
        : params === undefined
          ? (["expenses", companyId, resource] as const)
          : (["expenses", companyId, resource, params] as const),
  expenseDetail: (id?: string) => (id ? (["expense-detail", id] as const) : (["expense-detail"] as const)),
  /** Ödeme planlama: adaylar ve partiler ŞİRKET bazındadır. */
  payments: (companyId?: string | null, resource?: PaymentResource, params?: unknown) =>
    companyId === undefined
      ? (["payments"] as const)
      : resource === undefined
        ? (["payments", companyId] as const)
        : params === undefined
          ? (["payments", companyId, resource] as const)
          : (["payments", companyId, resource, params] as const),
  paymentBatch: (id?: string) => (id ? (["payment-batch", id] as const) : (["payment-batch"] as const)),
  /** Vergi ve beyan takvimi: kurallar, yükümlülükler ve KDV özeti ŞİRKET bazındadır (şablonlar platformdan). */
  taxCalendar: (companyId?: string | null, resource?: TaxCalendarResource, params?: unknown) =>
    companyId === undefined
      ? (["tax-calendar"] as const)
      : resource === undefined
        ? (["tax-calendar", companyId] as const)
        : params === undefined
          ? (["tax-calendar", companyId, resource] as const)
          : (["tax-calendar", companyId, resource, params] as const),
  /** Nakit akış: ayarlar ve projeksiyon ŞİRKET bazındadır (istasyon görünümü sorgu parametresidir). */
  cashFlow: (companyId?: string | null, resource?: CashFlowResource, params?: unknown) =>
    companyId === undefined
      ? (["cash-flow"] as const)
      : resource === undefined
        ? (["cash-flow", companyId] as const)
        : params === undefined
          ? (["cash-flow", companyId, resource] as const)
          : (["cash-flow", companyId, resource, params] as const),
  /** Bütçe: liste ve rapor ŞİRKET bazındadır. */
  budgets: (companyId?: string | null, resource?: BudgetResource, params?: unknown) =>
    companyId === undefined
      ? (["budgets"] as const)
      : resource === undefined
        ? (["budgets", companyId] as const)
        : params === undefined
          ? (["budgets", companyId, resource] as const)
          : (["budgets", companyId, resource, params] as const),
  /** Bütçe başlığı ve satırları kimlikle anahtarlanır (bağlantıyla açılan bütçenin şirketi yanıt gelmeden bilinmez). */
  budgetDetail: (kind: "budget" | "lines", id?: string) =>
    id ? (["budget-detail", kind, id] as const) : (["budget-detail"] as const),
  /**
   * Akaryakıt operasyonu: tank durumu, seviye serisi, satış özetleri, işlemler ve endeks İSTASYON,
   * ayarlar ŞİRKET bazındadır — ikinci parça bu yüzden "kapsam kimliği"dir (istasyon ya da şirket).
   * `["fuel"]` öneki modülün tümünü geçersiz kılar: elle girilen bir okuma hem tank durumunu hem seriyi değiştirir.
   */
  fuel: (scopeId?: string | null, resource?: FuelResource, params?: unknown) =>
    scopeId === undefined
      ? (["fuel"] as const)
      : resource === undefined
        ? (["fuel", scopeId] as const)
        : params === undefined
          ? (["fuel", scopeId, resource] as const)
          : (["fuel", scopeId, resource, params] as const),
  /**
   * Vardiya ve gün sonu: ayar ŞİRKET, personel / şablon / pano / liste İSTASYON, vardiya içi kayıtlar VARDİYA
   * bazındadır — ikinci parça bu yüzden "kapsam kimliği"dir. `["shifts"]` öneki modülün tümünü geçersiz kılar:
   * bir ara teslim hem vardiya ayrıntısını, hem panoyu, hem de kasa bakiyesini etkiler.
   */
  shifts: (scopeId?: string | null, resource?: ShiftResource, params?: unknown) =>
    scopeId === undefined
      ? (["shifts"] as const)
      : resource === undefined
        ? (["shifts", scopeId] as const)
        : params === undefined
          ? (["shifts", scopeId, resource] as const)
          : (["shifts", scopeId, resource, params] as const),
  /**
   * İstasyon ajanı: özet ve liste ŞİRKET, ayrıntı / heartbeat / komut / alarm / günlük AJAN bazındadır —
   * ikinci parça bu yüzden "kapsam kimliği"dir (şirket ya da ajan). `["agents"]` öneki modülün tümünü
   * geçersiz kılar: bir heartbeat hem listeyi, hem özeti, hem ayrıntıyı değiştirir.
   */
  agents: (scopeId?: string | null, resource?: AgentResource, params?: unknown) =>
    scopeId === undefined
      ? (["agents"] as const)
      : resource === undefined
        ? (["agents", scopeId] as const)
        : params === undefined
          ? (["agents", scopeId, resource] as const)
          : (["agents", scopeId, resource, params] as const),
  /**
   * Yerel kasa (Prompt 15): satış listesi, özet raporu, kampanyalar ve ayarlar ŞİRKET; fiş ayrıntısı SATIŞ,
   * PIN durumu PERSONEL bazındadır — ikinci parça bu yüzden "kapsam kimliği"dir. `["pos"]` öneki modülün
   * tümünü geçersiz kılar: ajandan düşen tek bir satış hem listeyi, hem raporu, hem kasiyer hesabını değiştirir.
   */
  pos: (scopeId?: string | null, resource?: PosResource, params?: unknown) =>
    scopeId === undefined
      ? (["pos"] as const)
      : resource === undefined
        ? (["pos", scopeId] as const)
        : params === undefined
          ? (["pos", scopeId, resource] as const)
          : (["pos", scopeId, resource, params] as const),
  /**
   * Market (Prompt 16): stok, hareket, parti, transfer, fire, sayım, gün kapanışı, sipariş, mal kabul, eşleşme,
   * lisans, rapor ve ayarlar ŞİRKET; belge ayrıntısı (transfer, fire, sayım, sipariş, mal kabul, eşleşme) BELGE, stok
   * kartı ÜRÜN, depo listesi, sipariş / fiyat önerisi ve etiket listesi İSTASYON bazındadır — ikinci parça bu yüzden
   * "kapsam kimliği"dir. `["market"]` öneki modülün tümünü geçersiz kılar: tek bir transfer kabulü hem iki
   * istasyonun stoğunu, hem hareket defterini, hem partileri, hem belgenin kendisini değiştirir.
   */
  market: (scopeId?: string | null, resource?: MarketResource, params?: unknown) =>
    scopeId === undefined
      ? (["market"] as const)
      : resource === undefined
        ? (["market", scopeId] as const)
        : params === undefined
          ? (["market", scopeId, resource] as const)
          : (["market", scopeId, resource, params] as const),
  /**
   * Müşteriler (Prompt 17): profil, araç ve müşteri 360 KİRACI düzeyindedir (şirket kırılımı yoktur — kart her
   * istasyonda geçer). `["customers"]` öneki modülün tümünü geçersiz kılar: bir rıza kaydı hem profili hem 360'ı değiştirir.
   */
  customers: (resource?: CustomerResource, params?: unknown) =>
    resource === undefined
      ? (["customers"] as const)
      : params === undefined
        ? (["customers", resource] as const)
        : (["customers", resource, params] as const),
  /**
   * KVKK (Prompt 17): metinler, rıza / ileti izni defterleri, başvurular, pazarlama günlüğü ve ayarlar KİRACI
   * düzeyindedir ve özellik bayrağıyla KAPANMAZ. `["privacy"]` öneki modülün tümünü geçersiz kılar.
   */
  privacy: (resource?: PrivacyResource, params?: unknown) =>
    resource === undefined
      ? (["privacy"] as const)
      : params === undefined
        ? (["privacy", resource] as const)
        : (["privacy", resource, params] as const),
  /**
   * Filo (Prompt 17): hesap, kural, araç, sürücü, etiket, kullanım defteri, ekstre, dönem faturası ve araç talepleri
   * KİRACI düzeyindedir (görünürlük sunucuda şirket / istasyon kapsamıyla süzülür). `["fleet"]` öneki modülün tümünü
   * geçersiz kılar: tek bir filo fişi hem kullanımı, hem kalan limiti, hem ekstreyi, hem dönem faturası önizlemesini değiştirir.
   */
  fleet: (resource?: FleetResource, params?: unknown) =>
    resource === undefined
      ? (["fleet"] as const)
      : params === undefined
        ? (["fleet", resource] as const)
        : (["fleet", resource, params] as const),
  /** Filo portalı (`/portal`): cari kimliği sunucuda kullanıcının CARİ kapsamlı atamasından türetilir. */
  fleetPortal: (resource?: FleetPortalResource, params?: unknown) =>
    resource === undefined
      ? (["fleet-portal"] as const)
      : params === undefined
        ? (["fleet-portal", resource] as const)
        : (["fleet-portal", resource, params] as const),
  /**
   * Sadakat (Prompt 17): program / seviye / kural / hesap / inceleme KİRACI, kupon şablonu / plan / abonelik / raporlar
   * ŞİRKET düzeyindedir (şirket kimliği parametrededir). `["loyalty"]` öneki modülün tümünü geçersiz kılar: tek bir kasa
   * satışı hem müşterinin bakiyesini, hem defteri, hem yükümlülüğü, hem kupon / abonelik kullanımını değiştirir.
   */
  loyalty: (resource?: LoyaltyResource, params?: unknown) =>
    resource === undefined
      ? (["loyalty"] as const)
      : params === undefined
        ? (["loyalty", resource] as const)
        : (["loyalty", resource, params] as const),
  /**
   * Yıkama (Prompt 18): ayar / program KİRACI, ünite / döngü / aktivasyon / kredi / kimyasal / randevu / iş emri / sıra /
   * rapor İSTASYON düzeyindedir (istasyon kimliği parametrededir). `["wash"]` öneki modülün tümünü geçersiz kılar: tek bir
   * makine döngüsü hem ünite panosunu (bugünkü sayı), hem döngü listesini, hem kimyasal stoğunu, hem raporu değiştirir.
   */
  wash: (resource?: WashResource, params?: unknown) =>
    resource === undefined
      ? (["wash"] as const)
      : params === undefined
        ? (["wash", resource] as const)
        : (["wash", resource, params] as const),
  /**
   * Şarj (Prompt 19): pano, ünite, oturum, komut, yük ve rapor İSTASYON düzeyindedir (istasyon kimliği parametrededir);
   * tarife şirket, dışa aktarım profili kiracı düzeyindedir. `["ev"]` öneki modülün tümünü geçersiz kılar: tek bir oturum
   * hem panoyu, hem ünitenin canlı oturumunu, hem oturum listesini, hem yük görünümünü, hem raporu değiştirir.
   */
  ev: (resource?: EvResource, params?: unknown) =>
    resource === undefined
      ? (["ev"] as const)
      : params === undefined
        ? (["ev", resource] as const)
        : (["ev", resource, params] as const),
  /**
   * Alarmlar (Prompt 20): liste, özet (ana sayfa kartı), ayrıntı, atanabilir kişiler ve kural ayarları KİRACI düzeyindedir
   * (görünürlük sunucuda kural izni + kapsamla süzülür). `["alarms"]` öneki modülün tümünü geçersiz kılar: tek bir
   * `alarms.alarm.*` olayı hem listeyi, hem özet sayılarını, hem ayrıntının zaman çizelgesini değiştirir.
   */
  alarms: (resource?: AlarmResource, params?: unknown) =>
    resource === undefined
      ? (["alarms"] as const)
      : params === undefined
        ? (["alarms", resource] as const)
        : (["alarms", resource, params] as const),
  /**
   * Bildirimler (Prompt 20) KİŞİSELDİR: kutu, okunmamış sayısı (zil), tercihler, ayarlar, teslim günlüğü ve özet
   * önizlemesi oturum kullanıcısınındır. `["notifications"]` öneki hepsini tazeler (`notifications.notification.created`
   * kullanıcı odasına gelir).
   */
  /**
   * Raporlama (Prompt 22): panolar (ana sayfa, canlı bugün, karşılaştırma, akaryakıt analitiği, finans, tutarlılık) kiracı
   * düzeyindedir (kapsam sunucuda); kaydedilmiş filtre / favori KİŞİSEL; ayarlar ve zamanlamalar kiracı. `["reports"]` öneki
   * hepsini tazeler.
   */
  reports: (resource?: ReportResource, params?: unknown) =>
    resource === undefined
      ? (["reports"] as const)
      : params === undefined
        ? (["reports", resource] as const)
        : (["reports", resource, params] as const),
  /**
   * Varlık bakımı (Prompt 23): varlıklar, planlar, iş emirleri, ayarlar ve rapor İSTASYON kapsamlıdır (sunucu süzer).
   * `["maintenance"]` öneki modülün tümünü geçersiz kılar: tek bir iş emri durumu hem listeyi, hem varlık kartını, hem plan
   * vadesini, hem raporu değiştirir.
   */
  maintenance: (resource?: MaintenanceResource, params?: unknown) =>
    resource === undefined
      ? (["maintenance"] as const)
      : params === undefined
        ? (["maintenance", resource] as const)
        : (["maintenance", resource, params] as const),
  /** Uyum takvimi (Prompt 23): türler, belgeler, özet, denetimler ve takvim. `["compliance"]` öneki hepsini tazeler. */
  compliance: (resource?: ComplianceResource, params?: unknown) =>
    resource === undefined
      ? (["compliance"] as const)
      : params === undefined
        ? (["compliance", resource] as const)
        : (["compliance", resource, params] as const),
  notifications: (resource?: NotificationResource, params?: unknown) =>
    resource === undefined
      ? (["notifications"] as const)
      : params === undefined
        ? (["notifications", resource] as const)
        : (["notifications", resource, params] as const),
  /** Platform paneli (Prompt 24). `["platform"]` öneki panelin tüm önbelleğini tazeler. */
  platform: (resource?: PlatformResource, params?: unknown) =>
    resource === undefined
      ? (["platform"] as const)
      : params === undefined
        ? (["platform", resource] as const)
        : (["platform", resource, params] as const),
  /** Kiracı tarafında platform ilişkisi (Prompt 24): aboneliğim, destek erişimi, dışa aktarım, devir, duyurular. */
  account: (resource?: AccountResource, params?: unknown) =>
    resource === undefined
      ? (["account"] as const)
      : params === undefined
        ? (["account", resource] as const)
        : (["account", resource, params] as const),
};

export type PlatformResource =
  | "me"
  | "tenants"
  | "tenant"
  | "tenant-features"
  | "tenant-stations"
  | "subscription"
  | "usage"
  | "preview"
  | "billing-profile"
  | "invoices"
  | "invoice"
  | "payments"
  | "plans"
  | "settings"
  | "rollouts"
  | "agents"
  | "releases"
  | "health"
  | "announcements"
  | "transfers"
  | "support-grants"
  | "support-events"
  | "exports"
  | "closure"
  | "staff";
export type AccountResource =
  | "subscription"
  | "invoice"
  | "exports"
  | "support-access"
  | "support-events"
  | "transfers"
  | "announcements";

export type AlarmResource = "list" | "summary" | "detail" | "assignable-users" | "rules";
export type MaintenanceResource =
  "assets" | "asset" | "plans" | "work-orders" | "work-order" | "assignees" | "settings" | "report";
export type ComplianceResource =
  | "catalog"
  | "types"
  | "documents"
  | "document"
  | "summary"
  | "responsibles"
  | "inspections"
  | "inspection"
  | "calendar";

export type ReportResource =
  | "home"
  | "live-today"
  | "comparison"
  | "fuel"
  | "finance"
  | "consistency"
  | "profitability"
  | "catalog"
  | "filters"
  | "favorites"
  | "settings"
  | "schedules"
  | "schedule-runs"
  | "recipients";

export type NotificationResource =
  "list" | "unread-count" | "preferences" | "settings" | "deliveries" | "digest-preview";

export type EvResource =
  | "pickers"
  | "stations"
  | "overview"
  | "chargers"
  | "charger"
  | "commands"
  | "sessions"
  | "session"
  | "tariffs"
  | "settings"
  | "load"
  | "partner-imports"
  | "export-profiles"
  | "energy"
  | "occupancy"
  | "availability"
  | "customer-spend";

export type WashResource =
  | "settings"
  | "stations"
  | "units"
  | "programs"
  | "program"
  | "products"
  | "meters"
  | "staff"
  | "customers"
  | "chemicals"
  | "activations"
  | "activation"
  | "cycles"
  | "credits"
  | "credit"
  | "day-closings"
  | "summary"
  | "occupancy"
  | "faults"
  | "appointments"
  | "availability"
  | "work-orders"
  | "work-order"
  | "queue";

export type LoyaltyResource =
  | "program"
  | "tiers"
  | "earn-rules"
  | "account"
  | "entries"
  | "reviews"
  | "coupon-campaigns"
  | "coupon-campaign"
  | "coupon-codes"
  | "coupon"
  | "customer-coupons"
  | "engine-campaigns"
  | "plans"
  | "plan"
  | "subscriptions"
  | "subscription"
  | "usages"
  | "liability"
  | "summary"
  | "logo";

export type CustomerResource =
  "list" | "customer" | "overview" | "vehicles" | "consents" | "message-permissions" | "stations";

export type PrivacyResource =
  "texts" | "text" | "requests" | "request" | "marketing-messages" | "settings" | "consents";

export type FleetResource =
  | "accounts"
  | "account"
  | "policy"
  | "vehicle-policy"
  | "effective-policy"
  | "vehicles"
  | "drivers"
  | "tags"
  | "usage"
  | "statement"
  | "consumption"
  | "billing-runs"
  | "billing-run"
  | "billing-preview"
  | "vehicle-requests"
  | "portal-users";

export type FleetPortalResource =
  "accounts" | "summary" | "vehicles" | "transactions" | "invoices" | "vehicle-requests" | "products";

export type PosResource = "sales" | "sale" | "report" | "settings" | "campaigns" | "campaign" | "staff-pin";

export type MarketResource =
  | "settings"
  | "warehouses"
  | "stock"
  | "movements"
  | "lots"
  | "stock-card"
  | "transfers"
  | "transfer"
  | "write-offs"
  | "write-off"
  | "counts"
  | "count"
  | "cycle-suggestions"
  | "daily-closings"
  | "product-suppliers"
  | "order-suggestions"
  | "purchase-orders"
  | "purchase-order"
  | "goods-receipts"
  | "goods-receipt"
  | "invoice-matches"
  | "invoice-match"
  | "match-candidates"
  | "price-suggestions"
  | "labels"
  | "licenses"
  | "reports";

export type AgentResource =
  "summary" | "list" | "agent" | "heartbeats" | "commands" | "alarms" | "log-uploads" | "enrollment-codes";

export type ShiftResource =
  | "settings"
  | "staff"
  | "templates"
  | "list"
  | "board"
  | "shift"
  | "cash-drops"
  | "cash-expenses"
  | "credit-sales"
  | "credit-counterparties"
  | "test-draws"
  | "closings"
  | "closing"
  | "report"
  | "days"
  | "day"
  | "day-checklist"
  | "day-corrections"
  | "z-reports"
  | "z-report"
  | "fiscal-devices"
  | "reports";

export type FuelResource =
  | "settings"
  | "tanks"
  | "tank-series"
  | "sales-daily"
  | "sales-hourly"
  | "transactions"
  | "totalizer-readings"
  | "orders"
  | "order"
  | "deliveries"
  | "delivery"
  | "reconciliations"
  | "cumulative"
  | "stock-costs"
  | "cost-history"
  | "daily-closings"
  | "totalizer-report"
  | "totalizer-adjustments"
  | "price-change-report"
  | "order-suggestions";

export type CashFlowResource = "settings" | "projection" | "compare" | "items";

export type BudgetResource = "list" | "report";

export type ApprovalResource = "requests" | "pending-count" | "policies" | "delegations";

export type ExpenseResource =
  | "list"
  | "categories"
  | "allocation-keys"
  | "allocation-preview"
  | "meters"
  | "readings"
  | "unit-costs"
  | "company-cards"
  | "recurring"
  | "preview";

export type PaymentResource = "candidates" | "batches" | "bank-file-preview";

export type TaxCalendarResource = "templates" | "rules" | "obligations" | "vat-summary";

export type InvoicingResource =
  | "settings"
  | "series"
  | "provider"
  | "taxpayer"
  | "invoices"
  | "preview"
  | "despatches"
  | "incoming"
  | "earchive-requests";

export type InvoicingDetailKind =
  "invoice" | "submissions" | "despatch" | "incoming" | "booking-suggestion" | "earchive-request";

export type InstrumentResource =
  | "list"
  | "eligible"
  | "batches"
  | "settings"
  | "books"
  | "leaves"
  | "unused-leaves"
  | "calendar"
  | "portfolio"
  | "maturity"
  | "payment-plan"
  | "counterparty-risk"
  | "drawers"
  | "reconciliation";

export type TreasuryResource =
  | "settings"
  | "cash-registers"
  | "cash-register"
  | "bank-accounts"
  | "bank-account"
  | "pos-terminals"
  | "transactions"
  | "transaction"
  | "cash-counts"
  | "cash-book"
  | "bank-reconciliation"
  | "cash-position"
  | "statement-lines"
  | "matching-rules"
  | "pos-batches"
  | "pos-value-calendar"
  | "pos-commission"
  | "guarantees"
  | "guarantee-summary"
  | "dbs-agreements"
  | "dbs-items"
  | "loans";

export type CounterpartyResource =
  | "list"
  | "search"
  | "risk"
  | "transactions"
  | "open-items"
  | "allocations"
  | "letters"
  | "notes"
  | "statement"
  | "aging"
  | "due-calendar"
  | "ba-bs"
  | "risk-board"
  | "ledger-reconciliation";

export type AccountingResource =
  | "settings"
  | "accounts"
  | "account-usage"
  | "cost-centers"
  | "stations"
  | "fiscal-years"
  | "checklist"
  | "entries"
  | "line-sources"
  | "posting-keys"
  | "mappings"
  | "exceptions"
  | "reports"
  | "export-adapters";
